import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../index.css';

const Leaderboard = () => {
  const [activeTab, setActiveTab] = useState('most-active');
  const [leaderboardData, setLeaderboardData] = useState({
    mostActive: [],
    topReviewers: [],
    giveawayHosters: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLeaderboardData();
  }, []);

  const fetchLeaderboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get('/api/leaderboard');
      setLeaderboardData(response.data.data);
    } catch (error) {
      console.error('Error fetching leaderboard data:', error);
      setError('Failed to load leaderboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatAccountType = (type) => {
    switch (type) {
      case 'indie_dev':
        return 'Indie Dev';
      case 'content_creator':
        return 'Content Creator';
      case 'giveaway_host':
        return 'Giveaway Host';
      case 'steam_curator':
        return 'Steam Curator';
      case 'admin':
        return 'Admin';
      default:
        return 'Member';
    }
  };

  const getAccountTypeColor = (type) => {
    switch (type) {
      case 'admin':
        return '#ff4757';
      case 'indie_dev':
        return '#3742fa';
      case 'content_creator':
        return '#ffa502';
      case 'giveaway_host':
        return '#3B82F6';
      case 'steam_curator':
        return '#1B2838';
      default:
        return '#7bed9f';
    }
  };

  const renderUserCard = (user, index, showScore = false, score = null, extraInfo = null) => (
    <div
      key={user._id}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '16px',
        backgroundColor: 'var(--card-bg)',
        borderRadius: '12px',
        marginBottom: '12px',
        border: '1px solid var(--border)',
        transition: 'all 0.2s ease',
        cursor: 'pointer'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateY(-2px)';
        e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = 'none';
      }}
      onClick={() => window.open(`/profile/${user.username}`, '_blank')}
    >
      {/* Rank */}
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: index < 3 ? '#ffd700' : 'var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: '16px',
        color: index < 3 ? '#000' : 'var(--text)',
        marginRight: '16px',
        flexShrink: 0
      }}>
        {index + 1}
      </div>

      {/* Avatar */}
      <div style={{
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        backgroundColor: 'var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '16px',
        flexShrink: 0,
        overflow: 'hidden'
      }}>
        {user.avatar ? (
          <img
            src={user.avatar}
            alt={user.username}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <span style={{ fontSize: '20px', color: 'var(--text-secondary)' }}>
            {user.username.charAt(0).toUpperCase()}
          </span>
        )}
      </div>

      {/* User Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: '600',
          fontSize: '16px',
          color: 'var(--text)',
          marginBottom: '4px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {user.username}
        </div>
        <div style={{
          fontSize: '12px',
          color: getAccountTypeColor(user.accountType),
          fontWeight: '500'
        }}>
          {formatAccountType(user.accountType)}
        </div>
      </div>

      {/* Score/Stats */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {showScore && (
          <div style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: 'var(--primary)',
            marginBottom: '2px'
          }}>
            {score}
          </div>
        )}
        {extraInfo && (
          <div style={{
            fontSize: '12px',
            color: 'var(--text-secondary)'
          }}>
            {extraInfo}
          </div>
        )}
      </div>
    </div>
  );

  const renderMostActiveTab = () => (
    <div>
      <div style={{
        textAlign: 'center',
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: 'var(--card-bg)',
        borderRadius: '12px',
        border: '1px solid var(--border)'
      }}>
        <h2 style={{ margin: '0 0 8px 0', color: 'var(--text)' }}>🏆 Most Active Users</h2>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
          Ranked by current available points (decreases when spent in shop)
        </p>
      </div>

      {leaderboardData.mostActive.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--text-secondary)'
        }}>
          No activity data available yet
        </div>
      ) : (
        leaderboardData.mostActive.map((item, index) =>
          renderUserCard(item.user, index, true, item.score, 'Activity Score')
        )
      )}
    </div>
  );

  const renderTopReviewersTab = () => (
    <div>
      <div style={{
        textAlign: 'center',
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: 'var(--card-bg)',
        borderRadius: '12px',
        border: '1px solid var(--border)'
      }}>
        <h2 style={{ margin: '0 0 8px 0', color: 'var(--text)' }}>⭐ Top Reviewers</h2>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
          Users with the most approved reviews and highest average ratings
        </p>
      </div>

      {leaderboardData.topReviewers.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--text-secondary)'
        }}>
          No reviews available yet
        </div>
      ) : (
        leaderboardData.topReviewers.map((item, index) =>
          renderUserCard(
            item.user,
            index,
            true,
            item.reviewCount,
            `Avg: ${item.averageRating}⭐`
          )
        )
      )}
    </div>
  );

  const renderGiveawayHostersTab = () => (
    <div>
      <div style={{
        textAlign: 'center',
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: 'var(--card-bg)',
        borderRadius: '12px',
        border: '1px solid var(--border)'
      }}>
        <h2 style={{ margin: '0 0 8px 0', color: 'var(--text)' }}>🎉 Giveaway Hosters</h2>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
          Users who have hosted the most giveaways for the community
        </p>
      </div>

      {leaderboardData.giveawayHosters.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--text-secondary)'
        }}>
          No giveaway hosters yet
        </div>
      ) : (
        leaderboardData.giveawayHosters.map((item, index) =>
          renderUserCard(
            item.user,
            index,
            true,
            item.giveawaysHosted,
            item.giveawaysHosted === 1 ? 'giveaway hosted' : 'giveaways hosted'
          )
        )
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid var(--border)',
            borderTop: '4px solid var(--primary)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: 'var(--text-secondary)' }}>Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '20px',
          backgroundColor: 'var(--card-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)'
        }}>
          <p style={{ color: '#ff4757', margin: '0 0 16px 0' }}>⚠️ {error}</p>
          <button
            onClick={fetchLeaderboardData}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '32px'
      }}>
        <h1 style={{
          fontSize: '2.5rem',
          margin: '0 0 8px 0',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          🏅 Leaderboards
        </h1>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '16px',
          margin: 0
        }}>
          Discover our most active community members and top contributors
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '32px',
        backgroundColor: 'var(--card-bg)',
        borderRadius: '12px',
        padding: '4px',
        border: '1px solid var(--border)'
      }}>
        {[
          { id: 'most-active', label: 'Most Active', icon: '🏆' },
          { id: 'top-reviewers', label: 'Top Reviewers', icon: '⭐' },
          { id: 'giveaway-hosters', label: 'Giveaway Hosters', icon: '🎉' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: activeTab === tab.id ? 'var(--primary)' : 'transparent',
              color: activeTab === tab.id ? 'white' : 'var(--text)',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'most-active' && renderMostActiveTab()}
        {activeTab === 'top-reviewers' && renderTopReviewersTab()}
        {activeTab === 'giveaway-hosters' && renderGiveawayHostersTab()}
      </div>

      {/* Points System Explanation - Below Leaderboard */}
      <div style={{
        marginTop: '40px',
        marginBottom: '32px',
        padding: '30px',
        backgroundColor: 'var(--card-bg)',
        borderRadius: '12px',
        border: '1px solid var(--border)',
        lineHeight: '1.7'
      }}>
        <h2 style={{
          color: 'var(--text-accent)',
          fontSize: '22px',
          fontWeight: '700',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          🎯 How Points Work
        </h2>
        
        <div style={{ color: 'var(--text)', fontSize: '15px' }}>
          <p style={{ marginBottom: '16px' }}>
            I reward people for being active. Engage authentically, earn points, unlock stuff in the shop. Pretty straightforward.
          </p>

          <h3 style={{ 
            color: 'var(--primary)', 
            fontSize: '18px', 
            fontWeight: '600',
            marginTop: '20px',
            marginBottom: '12px' 
          }}>
            💰 Earning Points
          </h3>
          
          <div style={{ paddingLeft: '20px', marginBottom: '16px' }}>
            <p style={{ marginBottom: '10px' }}>
              <strong>Daily Rewards (50-100 points):</strong> Log in every day and claim your bonus. Build a streak and you get more. 
              Easy way to rack up points consistently.
            </p>
            
            <p style={{ marginBottom: '10px' }}>
              <strong>Reviews (25-100 points):</strong> Write honest game reviews. Detailed ones earn more than short ones. Help other 
              players and developers, get rewarded for it.
            </p>
            
            <p style={{ marginBottom: '10px' }}>
              <strong>Community Cards (15-50 points):</strong> Post interesting stuff. Comments and engagement count too. Be active, 
              earn points.
            </p>
            
            <p style={{ marginBottom: '10px' }}>
              <strong>Giveaways (10-200 points):</strong> Entering costs points, winning gives you way more. Hosting giveaways boosts 
              your ranking too.
            </p>
            
            <p style={{ marginBottom: '10px' }}>
              <strong>Achievements:</strong> Complete milestones for bonus points. Check your achievements page to see what you can unlock.
            </p>
          </div>

          <h3 style={{ 
            color: 'var(--primary)', 
            fontSize: '18px', 
            fontWeight: '600',
            marginTop: '20px',
            marginBottom: '12px' 
          }}>
            🛍️ Using Points
          </h3>
          
          <div style={{ paddingLeft: '20px', marginBottom: '16px' }}>
            <p style={{ marginBottom: '10px' }}>
              Redeem points in the shop for game keys (Steam, Epic, GOG), profile customization (badges, titles, themes), featured 
              placement for your content/games, Discord perks, and early access to new features. Shop gets updated regularly. Points 
              never expire, so save up or spend freely.
            </p>
          </div>

          <h3 style={{ 
            color: 'var(--primary)', 
            fontSize: '18px', 
            fontWeight: '600',
            marginTop: '20px',
            marginBottom: '12px' 
          }}>
            📊 Leaderboard
          </h3>
          
          <div style={{ paddingLeft: '20px', marginBottom: '16px' }}>
            <p style={{ marginBottom: '10px' }}>
              Three categories: <strong>Most Active</strong> (overall engagement), <strong>Top Reviewers</strong> (quality and quantity 
              of reviews), and <strong>Giveaway Hosters</strong> (giving back to community). Rankings update regularly. Be consistent, 
              not sporadic. Top users get visibility and sometimes partnerships with developers.
            </p>
          </div>

          <div style={{
            marginTop: '20px',
            padding: '16px',
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '8px',
            border: '1px solid var(--primary)',
            fontSize: '14px'
          }}>
            <p style={{ marginBottom: '8px', fontWeight: '600', color: 'var(--primary)' }}>
              🚫 Fair Play
            </p>
            <p style={{ margin: '0', color: 'var(--text-secondary)' }}>
              System rewards real engagement, not spam. Fake reviews or bots get you banned and points removed. Keep it genuine.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;